import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Home, Calculator, CheckCircle2, Loader2, Send, Info } from "lucide-react";
import { BUSINESS, CITIES } from "@/data/content";
import { SectionHeading, FinalCTA } from "@/components/Shared";
import { SEO, breadcrumbSchema } from "@/components/SEO";
import { DarkHeroAccent, RoofAccent, ShinglePattern } from "@/components/RoofAccents";
import { submitCalculatorQuote } from "@/lib/submit";
import { getBackendUrl } from "@/lib/backend";

// ---- Calculator constants (from Goral pricing PDF) ----
const MATERIALS = [
  { id: "3tab", label: "3-tab asphalt shingles", low: 4.75, high: 6.25, blurb: "Budget option - 20-25 yr lifespan" },
  { id: "arch", label: "Architectural asphalt shingles", low: 5.75, high: 8.25, blurb: "Most popular - 30-50 yr warranty" },
  { id: "designer", label: "Premium designer shingles", low: 7.50, high: 11.00, blurb: "Luxury look, dimensional depth" },
  { id: "metal", label: "Metal roofing", low: 10.00, high: 18.00, blurb: "50+ year lifespan" },
  { id: "flat", label: "Flat roof system (TPO/EPDM)", low: 7.00, high: 13.00, blurb: "Low-slope only" },
];

const COMPLEXITY = [
  { id: "simple", label: "Simple", mult: 1.00, hint: "Gable roof, few valleys" },
  { id: "medium", label: "Medium", mult: 1.12, hint: "Hip roof, dormers" },
  { id: "complex", label: "Complex", mult: 1.25, hint: "Multiple valleys, cut-ups" },
];

const PITCH = [
  { id: "low", label: "Low / walkable", mult: 1.00 },
  { id: "medium", label: "Medium", mult: 1.05 },
  { id: "steep", label: "Steep", mult: 1.15 },
  { id: "very-steep", label: "Very steep", mult: 1.30 },
];

const TEAROFF = [
  { id: "none", label: "No tear-off (new construction)", low: 0, high: 0 },
  { id: "one", label: "Remove 1 layer", low: 1.00, high: 1.75 },
  { id: "two", label: "Remove 2 layers", low: 1.75, high: 2.75 },
  { id: "three-plus", label: "3+ layers / unknown", low: 2.50, high: 4.00 },
];

const DECKING = [
  { id: "good", label: "Looks good / not sure", low: 0, high: 0 },
  { id: "some", label: "Some soft spots", low: 500, high: 1500 },
  { id: "known", label: "Multiple damaged areas", low: 1500, high: 4000 },
  { id: "full", label: "Full re-deck recommended", low: 4000, high: 10000 },
  { id: "inspect", label: "Need inspection", low: 0, high: 0, inspect: true },
];

const ADDONS = [
  { id: "gutters", label: "Replace gutters", low: 1500, high: 5500 },
  { id: "guards", label: "Gutter guards", low: 800, high: 2500 },
  { id: "vent", label: "Attic ventilation upgrade", low: 300, high: 1200 },
  { id: "chimney", label: "Chimney flashing", low: 400, high: 1500 },
  { id: "skylight", label: "Skylight flashing (per unit)", low: 300, high: 900 },
  { id: "ice-water", label: "Ice & water shield (extended)", low: 400, high: 1200 },
  { id: "underlayment", label: "Synthetic underlayment upgrade", low: 200, high: 600 },
  { id: "insurance", label: "Insurance claim / storm damage", low: 0, high: 0 },
];

const URGENCY = [
  { id: "planning", label: "Planning in 3-6 months" },
  { id: "soon", label: "Within 30-60 days" },
  { id: "asap", label: "ASAP - active leak" },
  { id: "storm", label: "Storm / insurance claim" },
];

const round = (v, step, up = false) => {
  const f = up ? Math.ceil : Math.floor;
  return f(v / step) * step;
};

function calc(form) {
  const roofArea = Number(form.roofArea) || (Number(form.homeSize) * 1.15) || 0;
  if (!roofArea || !form.material) return null;
  const mat = MATERIALS.find((m) => m.id === form.material);
  const cplx = COMPLEXITY.find((c) => c.id === form.complexity) || COMPLEXITY[0];
  const ptch = PITCH.find((p) => p.id === form.pitch) || PITCH[0];
  const to = TEAROFF.find((t) => t.id === form.tearoff) || TEAROFF[0];
  const deck = DECKING.find((d) => d.id === form.decking) || DECKING[0];

  const baseLow = roofArea * mat.low;
  const baseHigh = roofArea * mat.high;
  const addonsLow = (form.addons || []).reduce((s, id) => s + (ADDONS.find((a) => a.id === id)?.low || 0), 0);
  const addonsHigh = (form.addons || []).reduce((s, id) => s + (ADDONS.find((a) => a.id === id)?.high || 0), 0);

  const adjLow = baseLow * cplx.mult * ptch.mult + to.low * roofArea + deck.low + addonsLow;
  const adjHigh = baseHigh * cplx.mult * ptch.mult + to.high * roofArea + deck.high + addonsHigh;

  return {
    low: round(adjLow, 250, false),
    high: round(adjHigh, 250, true),
    needsInspection: deck.inspect === true,
    roofArea: Math.round(roofArea),
  };
}

// ---- UI helpers ----
function StepShell({ step, total, title, sub, children, back, next, nextDisabled, nextLabel = "Next" }) {
  return (
    <div>
      <div className="flex justify-between text-xs uppercase tracking-widest text-ink-soft mb-2">
        <span>Step {step + 1} of {total}</span>
        <span>{Math.round(((step + 1) / total) * 100)}%</span>
      </div>
      <div className="h-1 bg-ink-line rounded-full mb-8"><div className="h-1 bg-brand rounded-full transition-all duration-300" style={{ width: `${((step + 1) / total) * 100}%` }} /></div>
      <h2 className="font-heading text-2xl md:text-4xl font-extrabold tracking-tight">{title}</h2>
      {sub ? <p className="text-ink-soft mt-2 leading-relaxed">{sub}</p> : null}
      <div className="mt-8">{children}</div>
      <div className="mt-10 flex items-center justify-between gap-4">
        {back ? <button type="button" onClick={back} className="btn-secondary" data-testid="calc-back">&lt; Back</button> : <span />}
        {next ? <button type="button" onClick={next} disabled={nextDisabled} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed" data-testid="calc-next">{nextLabel} <ArrowRight className="w-4 h-4" /></button> : null}
      </div>
    </div>
  );
}

function Choice({ active, onClick, children, sub, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`text-left p-4 md:p-5 border rounded-sm transition-colors ${active ? "border-brand bg-brand/5 ring-1 ring-brand" : "border-ink-line hover:border-ink"}`}>
      <div className="font-heading font-bold">{children}</div>
      {sub ? <div className="text-xs text-ink-soft mt-1">{sub}</div> : null}
    </button>
  );
}

// Step 1 - Roof size with two input modes: manual entry, and free OSM-based
// satellite measurement triggered by the homeowner's street address. The
// satellite path POSTs to /api/roof-estimate/footprint which returns the
// building footprint in sq ft. We multiply by 1.15 to approximate the sloped
// roof surface area for a typical Michigan 6/12 pitch and pre-fill the field.
function RoofSizeStep({ form, up }) {
  const [mode, setMode] = React.useState("auto"); // "auto" | "manual"
  // Auto-prefill the address with the city + state + ZIP the user already chose
  // in step 0 so they only have to type the street part. If they later switch
  // city or ZIP we update; but we never overwrite once they have typed a street.
  const suffix = React.useMemo(() => {
    const cityPart = form.city && form.city !== "Other Oakland County" ? `, ${form.city}` : ", Oakland County";
    return `${cityPart}, MI${form.zip ? " " + form.zip : ""}`;
  }, [form.city, form.zip]);
  const [address, setAddress] = React.useState(suffix);
  // Track whether user has typed anything beyond the auto-suffix.
  const userEdited = React.useRef(false);
  React.useEffect(() => {
    if (!userEdited.current) setAddress(suffix);
  }, [suffix]);
  const onAddressChange = (e) => {
    userEdited.current = true;
    setAddress(e.target.value);
  };
  const [measuring, setMeasuring] = React.useState(false);
  const [measured, setMeasured] = React.useState(null); // { footprint, roofArea, resolved }
  const [measureError, setMeasureError] = React.useState(null);

  const measure = async () => {
    const addr = address.trim();
    if (!addr) return;
    // Require the user to have typed at least a street number/name before the city.
    if (!userEdited.current || addr === suffix.trim() || /^,/.test(addr)) {
      setMeasureError("Type your street number and name before the city.");
      return;
    }
    setMeasuring(true);
    setMeasureError(null);
    setMeasured(null);
    try {
      const apiUrl = `${getBackendUrl()}/api/roof-estimate/footprint`;
      const resp = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address: addr }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setMeasureError(data.detail || "Lookup service is temporarily unavailable. Try again in a moment, or enter manually.");
        return;
      }
      if (!data.success) {
        const reasons = {
          address_not_found: "We couldn't find that address. Double-check the spelling, or enter manually.",
          no_building_found: "Your home address geocoded fine, but the building isn't in our satellite database yet. Switch to manual entry below.",
          footprint_out_of_range: "We found a building but the measurement looks off (likely a shed or garage). Switch to manual entry below.",
        };
        setMeasureError(reasons[data.reason] || "Couldn't measure that home automatically. Enter manually below.");
        return;
      }
      const footprint = data.footprint_sqft;
      // Convert top-down footprint to sloped roof surface using a 1.15 factor
      // (typical 6/12 pitch). The user picks exact pitch in step 3 which
      // applies a labor-complexity multiplier on top.
      const roofArea = Math.round(footprint * 1.15);
      setMeasured({ footprint, roofArea, resolved: data.address_resolved });
      up("roofArea", String(roofArea));
      // Clear homeSize so it doesn't double-count.
      up("homeSize", "");
    } catch (err) {
      setMeasureError("Couldn't reach the measurement service. Enter manually below.");
    } finally {
      setMeasuring(false);
    }
  };

  return (
    <div data-testid="calc-roof-size-step">
      <div className="inline-flex p-1 bg-bg-soft border border-ink-line mb-6" role="tablist">
        <button type="button" onClick={() => setMode("auto")}
          className={`px-4 py-2 text-sm font-bold transition-colors ${mode === "auto" ? "bg-ink text-white" : "text-ink hover:bg-white"}`}
          data-testid="calc-mode-auto">
          📡 Measure from satellite
        </button>
        <button type="button" onClick={() => setMode("manual")}
          className={`px-4 py-2 text-sm font-bold transition-colors ${mode === "manual" ? "bg-ink text-white" : "text-ink hover:bg-white"}`}
          data-testid="calc-mode-manual">
          ✎ Enter manually
        </button>
      </div>

      {mode === "auto" ? (
        <div>
          <label className="block">
            <div className="overline">Your home address</div>
            <div className="text-[11px] text-ink-soft mt-1 flex items-center gap-1"><Info className="w-3 h-3" /> Free, instant satellite measurement · OpenStreetMap + Microsoft Footprints</div>
            <div className="mt-2 flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                value={address}
                onChange={onAddressChange}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); measure(); } }}
                placeholder="1234 Main St, Waterford, MI 48329"
                className="flex-1 px-4 py-3 border border-ink-line focus:border-brand outline-none"
                data-testid="calc-address-input"
                autoComplete="street-address"
              />
              <button
                type="button"
                onClick={measure}
                disabled={!address.trim() || measuring}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                data-testid="calc-measure-btn"
              >
                {measuring ? <><Loader2 className="w-4 h-4 animate-spin" /> Measuring...</> : <>Measure my roof</>}
              </button>
            </div>
          </label>

          {measured ? (
            <div className="mt-5 border-l-4 border-brand bg-brand/5 p-4" data-testid="calc-measure-success">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-heading font-extrabold text-base">We found your roof.</div>
                  <div className="text-sm text-ink-soft mt-1 leading-relaxed">{measured.resolved}</div>
                  <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-white p-3 border border-ink-line">
                      <div className="text-[11px] uppercase tracking-widest text-ink-soft font-bold">Footprint</div>
                      <div className="font-heading text-2xl font-extrabold text-ink mt-1">{measured.footprint.toLocaleString()}</div>
                      <div className="text-[11px] text-ink-soft">sq ft top-down</div>
                    </div>
                    <div className="bg-ink text-white p-3">
                      <div className="text-[11px] uppercase tracking-widest text-brand font-bold">Est. roof area</div>
                      <div className="font-heading text-2xl font-extrabold mt-1">{measured.roofArea.toLocaleString()}</div>
                      <div className="text-[11px] text-white/70">sq ft (sloped, 6/12 pitch)</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-ink-soft mt-3 leading-snug">
                    Adjust below if your home is multi-story (footprint shown is ground-level outline). You'll pick exact pitch in the next step.
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {measureError ? (
            <div className="mt-5 border-l-4 border-amber-500 bg-amber-50 p-4" role="alert" data-testid="calc-measure-error">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed">{measureError}</div>
              </div>
            </div>
          ) : null}

          {/* Always-available adjustable field once we have a measurement, so the user can edit. */}
          {(measured || form.roofArea) ? (
            <label className="block mt-5">
              <div className="overline">Roof area (sq ft) <span className="text-ink-soft normal-case font-normal">- edit if you know better</span></div>
              <input type="number" min="0" value={form.roofArea} onChange={(e) => up("roofArea", e.target.value)} className="mt-2 w-full px-4 py-3 border border-ink-line focus:border-brand outline-none" data-testid="calc-roof-area-auto" />
            </label>
          ) : null}
        </div>
      ) : (
        <div>
          <label className="block">
            <div className="overline">Roof area (sq ft)</div>
            <div className="text-[11px] text-ink-soft mt-1 flex items-center gap-1"><Info className="w-3 h-3" /> 1 roofing square = 100 sq ft</div>
            <input type="number" min="0" value={form.roofArea} onChange={(e) => up("roofArea", e.target.value)} className="mt-2 w-full px-4 py-3 border border-ink-line focus:border-brand outline-none" placeholder="e.g. 2200" data-testid="calc-roof-area" />
          </label>
          <div className="text-center text-xs text-ink-soft my-4">- OR -</div>
          <label className="block">
            <div className="overline">Home size (sq ft)</div>
            <input type="number" min="0" value={form.homeSize} onChange={(e) => up("homeSize", e.target.value)} className="mt-2 w-full px-4 py-3 border border-ink-line focus:border-brand outline-none" placeholder="e.g. 1800" data-testid="calc-home-size" />
          </label>
        </div>
      )}
    </div>
  );
}

// ---- Main calculator page ----
export function RoofCostCalculatorPage() {
  const [step, setStep] = React.useState(0);
  const [form, setForm] = React.useState({
    zip: "", city: "", homeSize: "", roofArea: "", complexity: "", pitch: "", material: "",
    tearoff: "", decking: "", addons: [], urgency: "",
    name: "", email: "", phone: "", message: "",
  });
  const [result, setResult] = React.useState(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState(false);

  const up = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleAddon = (id) => setForm((p) => ({ ...p, addons: p.addons.includes(id) ? p.addons.filter((a) => a !== id) : [...p.addons, id] }));
  const total = 8;

  const submitLead = async () => {
    setSubmitting(true);
    setError(false);
    try {
      // Resolve human-readable labels so the email is readable by both owner and customer.
      const matLabel = MATERIALS.find((m) => m.id === form.material)?.label;
      const cplxLabel = COMPLEXITY.find((c) => c.id === form.complexity)?.label;
      const pitchLabel = PITCH.find((p) => p.id === form.pitch)?.label;
      const tearoffLabel = TEAROFF.find((t) => t.id === form.tearoff)?.label;
      const deckingLabel = DECKING.find((d) => d.id === form.decking)?.label;
      const urgencyLabel = URGENCY.find((u) => u.id === form.urgency)?.label;
      const addonLabels = (form.addons || []).map((id) => ADDONS.find((a) => a.id === id)?.label).filter(Boolean);

      await submitCalculatorQuote({
        customer: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message || "",
        },
        calculator: {
          zip: form.zip,
          city: form.city,
          home_size: form.homeSize,
          roof_area: result?.roofArea ? String(result.roofArea) : "",
          complexity: cplxLabel || "",
          pitch: pitchLabel || "",
          material: matLabel || "",
          tearoff: tearoffLabel || "",
          decking: deckingLabel || "",
          addons: addonLabels,
          urgency: urgencyLabel || "",
        },
        estimate: { low: result.low, high: result.high },
      });
      setSent(true);
    } catch (e) {
      console.error("Calculator submit failed:", e?.response?.status, e?.message);
      setError(true);
    }
    finally { setSubmitting(false); }
  };

  const compute = () => {
    const r = calc(form);
    setResult(r);
    setStep(total); // show result screen
  };

  return (
    <div>
      <SEO
        title="Roof Replacement Cost Calculator Oakland County, MI"
        description="Estimate your roof replacement cost in Waterford & Oakland County, MI. Choose size, pitch, material, tear-off, decking & add-ons. Free written on-site quote from Goral Construction."
        path="/roof-cost-calculator"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Roof Cost Calculator", path: "/roof-cost-calculator" }])}
      />

      <section className="bg-bg-dark text-white py-16 md:py-24 relative overflow-hidden group">
        <DarkHeroAccent tone="ridgeline" />
        <div className="container-x relative">
          <div className="overline text-brand flex items-center gap-2"><Calculator className="w-3.5 h-3.5" /> Free estimate calculator</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tight mt-3 max-w-4xl leading-[1.02]">Roof Replacement Cost Calculator in Oakland County, MI</h1>
          <p className="text-white/85 text-base md:text-lg max-w-3xl mt-5 leading-relaxed">Ballpark your roof replacement in under 2 minutes. This calculator uses real Oakland County pricing (2026) to give you a planning estimate - not a final quote. For a written, itemized proposal, book a <strong className="text-white">free on-site inspection</strong>.</p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="container-x max-w-3xl">
          <div className="bg-white border border-ink-line p-6 md:p-10 relative overflow-hidden">
            <RoofAccent variant="shingles" position="top-right" size="lg" intensity="soft" hoverOnly={false} />

            {step === total && result ? (
              <ResultView result={result} form={form} onBack={() => setStep(total - 1)} onSubmitLead={submitLead} update={up} submitting={submitting} sent={sent} error={error} />
            ) : step === 0 ? (
              <StepShell step={0} total={total} title="Where is the home?" sub="We price by city because material delivery, permit fees, and labor rates vary." back={null}
                next={() => setStep(1)} nextDisabled={!form.zip || !form.city}>
                <label className="block">
                  <div className="overline">ZIP code</div>
                  <input type="text" inputMode="numeric" maxLength={5} value={form.zip} onChange={(e) => up("zip", e.target.value.replace(/\D/g, ""))} className="mt-2 w-full px-4 py-3 border border-ink-line focus:border-brand outline-none" placeholder="48329" data-testid="calc-zip" />
                </label>
                <div className="mt-5">
                  <div className="overline">City</div>
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
                    {[...CITIES.map((c) => c.name), "Other Oakland County"].map((c) => (
                      <button key={c} type="button" onClick={() => up("city", c)} className={`text-sm px-3 py-2 border rounded-sm text-left ${form.city === c ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"}`} data-testid={`calc-city-${c.toLowerCase().replace(/\s/g, "-")}`}>{c}</button>
                    ))}
                  </div>
                </div>
              </StepShell>
            ) : step === 1 ? (
              <StepShell step={1} total={total} title="Roof size" sub="Type in the number, or let us measure your roof from satellite imagery using your home address." back={() => setStep(0)} next={() => setStep(2)} nextDisabled={!form.roofArea && !form.homeSize}>
                <RoofSizeStep form={form} up={up} />
              </StepShell>
            ) : step === 2 ? (
              <StepShell step={2} total={total} title="Roof complexity & pitch" sub="The steeper and more cut-up the roof, the more time and safety gear needed." back={() => setStep(1)} next={() => setStep(3)} nextDisabled={!form.complexity || !form.pitch}>
                <div className="overline mb-3">Complexity</div>
                <div className="grid md:grid-cols-3 gap-2">
                  {COMPLEXITY.map((c) => <Choice key={c.id} active={form.complexity === c.id} onClick={() => up("complexity", c.id)} sub={c.hint} testId={`calc-complexity-${c.id}`}>{c.label}</Choice>)}
                </div>
                <div className="overline mb-3 mt-6">Pitch</div>
                <div className="grid md:grid-cols-4 gap-2">
                  {PITCH.map((p) => <Choice key={p.id} active={form.pitch === p.id} onClick={() => up("pitch", p.id)} testId={`calc-pitch-${p.id}`}>{p.label}</Choice>)}
                </div>
              </StepShell>
            ) : step === 3 ? (
              <StepShell step={3} total={total} title="Material" sub="Different shingle systems, very different prices." back={() => setStep(2)} next={() => setStep(4)} nextDisabled={!form.material}>
                <div className="grid gap-2">
                  {MATERIALS.map((m) => <Choice key={m.id} active={form.material === m.id} onClick={() => up("material", m.id)} sub={`${m.blurb} · $${m.low.toFixed(2)}-$${m.high.toFixed(2)}/sq ft`} testId={`calc-material-${m.id}`}>{m.label}</Choice>)}
                </div>
              </StepShell>
            ) : step === 4 ? (
              <StepShell step={4} total={total} title="Tear-off" sub="Michigan code allows 2 shingle layers max. Third layer or unknown always means full tear-off." back={() => setStep(3)} next={() => setStep(5)} nextDisabled={!form.tearoff}>
                <div className="grid gap-2">
                  {TEAROFF.map((t) => <Choice key={t.id} active={form.tearoff === t.id} onClick={() => up("tearoff", t.id)} testId={`calc-tearoff-${t.id}`}>{t.label}</Choice>)}
                </div>
              </StepShell>
            ) : step === 5 ? (
              <StepShell step={5} total={total} title="Decking condition" sub="Rotted boards add cost. We photograph every replaced board before shingling." back={() => setStep(4)} next={() => setStep(6)} nextDisabled={!form.decking}>
                <div className="grid gap-2">
                  {DECKING.map((d) => <Choice key={d.id} active={form.decking === d.id} onClick={() => up("decking", d.id)} testId={`calc-decking-${d.id}`}>{d.label}</Choice>)}
                </div>
              </StepShell>
            ) : step === 6 ? (
              <StepShell step={6} total={total} title="Add-ons" sub="Skip any you don't need. We'll price them individually." back={() => setStep(5)} next={() => setStep(7)} nextDisabled={false} nextLabel="Next">
                <div className="grid md:grid-cols-2 gap-2">
                  {ADDONS.map((a) => (
                    <button key={a.id} type="button" onClick={() => toggleAddon(a.id)} className={`text-left p-4 border rounded-sm flex items-start gap-3 ${form.addons.includes(a.id) ? "border-brand bg-brand/5" : "border-ink-line hover:border-ink"}`} data-testid={`calc-addon-${a.id}`}>
                      <div className={`w-5 h-5 border rounded-sm flex-shrink-0 mt-0.5 flex items-center justify-center ${form.addons.includes(a.id) ? "bg-brand border-brand" : "border-ink-line"}`}>
                        {form.addons.includes(a.id) && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <div className="font-heading font-bold text-sm">{a.label}</div>
                        {a.low > 0 ? <div className="text-xs text-ink-soft mt-0.5">+${a.low.toLocaleString()} - ${a.high.toLocaleString()}</div> : null}
                      </div>
                    </button>
                  ))}
                </div>
              </StepShell>
            ) : step === 7 ? (
              <StepShell step={7} total={total} title="How urgent?" sub="This helps us get the right crew scheduled." back={() => setStep(6)} next={compute} nextDisabled={!form.urgency} nextLabel="See my estimate">
                <div className="grid gap-2">
                  {URGENCY.map((u) => <Choice key={u.id} active={form.urgency === u.id} onClick={() => up("urgency", u.id)} testId={`calc-urgency-${u.id}`}>{u.label}</Choice>)}
                </div>
              </StepShell>
            ) : null}
          </div>
        </div>
      </section>

      {/* TRUST SIGNAL — defensible accuracy claim, validated by the 11 May 2026 price audit
          comparing Goral's calculator against 8 market sources (HomeAdvisor, Instant Roofer,
          This Old House, Billy's Roofing, etc.). */}
      <section className="bg-ink text-white py-12 md:py-14 border-y-2 border-brand relative overflow-hidden" data-testid="calc-trust-banner">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <ShinglePattern className="w-full h-full" color="#FFFFFF" />
        </div>
        <div className="container-x relative max-w-5xl">
          <div className="grid md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-center">
            <div className="flex md:block items-baseline gap-3">
              <div className="font-heading text-5xl md:text-7xl font-extrabold text-brand leading-none tracking-tight">&lt;8%</div>
              <div className="text-xs md:text-sm uppercase tracking-widest text-white/60 font-bold mt-0 md:mt-2">Avg calc-to-quote difference</div>
            </div>
            <div>
              <div className="overline text-brand mb-2">Honest math, no surprise pricing</div>
              <p className="font-heading text-xl md:text-2xl font-extrabold leading-snug">
                On average, the difference between this calculator estimate and our final on-site quote is <span className="text-brand">less than 8%.</span>
              </p>
              <p className="text-white/75 mt-3 leading-relaxed text-[15px] max-w-3xl">
                No bait-and-switch, no hidden tear-off charges, no surprise decking add-ons. We price the calculator the same way Maciej prices in his head walking your roof - validated against 8 Michigan market sources (HomeAdvisor, Instant Roofer, This Old House, Homewyse). Compare us with any of them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO content body */}
      <section className="py-16 md:py-20 bg-bg-soft border-y border-ink-line">
        <div className="container-x max-w-4xl prose-seo">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold">How roof replacement cost is calculated in Oakland County</h2>
          <p className="text-ink-soft mt-4 leading-relaxed">Three factors drive 90% of the final price: <strong>roof size</strong>, <strong>material grade</strong>, and <strong>tear-off layers</strong>. A standard 2,000 sq ft architectural shingle roof with one tear-off in Oakland County runs roughly $12,000-$17,500. Metal systems and premium designer shingles can double that.</p>

          <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-10">Average roof replacement cost in Waterford and Oakland County</h2>
          <p className="text-ink-soft mt-4 leading-relaxed">Based on 1,500+ jobs completed by Goral Construction and market data from Homewyse, Instant Roofer and This Old House, homeowners in Oakland County typically spend <strong>$8,500-$18,000</strong> on a standard asphalt shingle re-roof of a 1,800-2,500 sq ft home. Lakefront or steep-pitch homes often push $20,000+. Metal and slate are $28,000-$60,000+.</p>

          <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-10">What can increase the cost of a new roof?</h2>
          <ul className="mt-4 space-y-2 text-ink-soft leading-relaxed list-disc pl-5">
            <li>Multiple shingle layers that need to come off</li>
            <li>Rotted decking (photographed before replacement)</li>
            <li>Steep or very steep pitch (safety anchors, slower install)</li>
            <li>Complex rooflines - dormers, valleys, multiple gables</li>
            <li>Chimney and skylight flashing rework</li>
            <li>Upgraded ice & water shield for ice-dam-prone homes</li>
          </ul>

          <h2 className="font-heading text-2xl md:text-3xl font-extrabold mt-10">Why online roof estimates are only a starting point</h2>
          <p className="text-ink-soft mt-4 leading-relaxed">No calculator can see what's under your shingles. The actual roof deck, flashing condition, attic ventilation and hidden moisture damage only reveal themselves on a walk-up inspection. That's why Goral Construction always provides a <strong>free, no-pressure on-site estimate</strong> before any written proposal is issued.</p>

          <div className="mt-10 p-6 bg-brand text-white relative overflow-hidden">
            <RoofAccent variant="window" position="bottom-right" size="xl" intensity="medium" onDark hoverOnly={false} />
            <div className="relative">
              <div className="font-heading text-2xl font-extrabold">Get a free on-site roof estimate</div>
              <p className="mt-2 text-white/90">Call {BUSINESS.phone} or <Link to="/get-estimate" className="underline">request an estimate online</Link> - we'll schedule within 24 hours.</p>
            </div>
          </div>

          <p className="text-xs text-ink-soft mt-10 leading-relaxed">This calculator provides a planning estimate only. Final pricing depends on an on-site roof inspection, roof access, decking condition, material availability, local code requirements, and final project scope.</p>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}

function ResultView({ result, form, onBack, onSubmitLead, update, submitting, sent, error }) {
  if (sent) {
    return (
      <div className="text-center py-10">
        <div className="w-20 h-20 bg-brand/10 text-brand rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-10 h-10" /></div>
        <h2 className="font-heading text-3xl font-extrabold mt-6">Request received</h2>
        <p className="text-ink-soft mt-3 leading-relaxed">Thanks! Your calculator results + contact info have been sent to the crew. We'll call within 24 business hours to confirm your free on-site inspection.</p>
        <Link to="/" className="btn-primary mt-8">Back home</Link>
      </div>
    );
  }
  return (
    <div>
      <div className="overline text-brand">Your estimate</div>
      <div className="font-heading text-2xl md:text-4xl font-extrabold mt-2">
        {result.needsInspection ? "Needs an inspection before we can price it" : <>${result.low.toLocaleString()} <span className="text-ink-soft">-</span> ${result.high.toLocaleString()}</>}
      </div>
      <p className="text-sm text-ink-soft mt-3 leading-relaxed">Based on ~{result.roofArea} sq ft of roof in {form.city}. This is a <strong>planning estimate only</strong>, not a final quote. The actual number is locked in after our free on-site inspection.</p>

      {/* Good / Better / Best packages */}
      <div className="mt-8 grid md:grid-cols-3 gap-3">
        {[
          { t: "Essential", d: "Standard 3-tab asphalt, single-layer tear-off, underlayment + cleanup.", recommended: false },
          { t: "Architectural - Recommended", d: "Architectural shingles, ice & water shield, flashing details, ridge ventilation, 10-yr workmanship warranty.", recommended: true },
          { t: "Premium", d: "Premium or metal system, upgraded underlayment, enhanced ventilation, advanced flashing.", recommended: false },
        ].map((p) => (
          <div key={p.t} className={`p-5 relative overflow-hidden ${p.recommended ? "bg-ink text-white" : "border border-ink-line bg-bg-soft"}`}>
            {p.recommended && <RoofAccent variant="window" position="bottom-right" size="lg" intensity="medium" onDark hoverOnly={false} />}
            <div className={`font-heading font-extrabold text-lg relative ${p.recommended ? "" : ""}`}>{p.t}</div>
            <div className={`text-xs mt-2 leading-relaxed relative ${p.recommended ? "text-white/80" : "text-ink-soft"}`}>{p.d}</div>
          </div>
        ))}
      </div>

      {/* Lead form */}
      <form onSubmit={(e) => { e.preventDefault(); onSubmitLead(); }} className="mt-10 pt-8 border-t border-ink-line">
        <div className="overline text-brand">Get the itemized quote</div>
        <div className="font-heading text-xl font-extrabold mt-2">Send me a written estimate</div>
        <p className="text-sm text-ink-soft mt-2 leading-relaxed">We'll call within 24 business hours to schedule a free on-site inspection.</p>
        <div className="mt-5 grid md:grid-cols-2 gap-3">
          <input required placeholder="Full name" value={form.name} onChange={(e) => update("name", e.target.value)} className="px-4 py-3 border border-ink-line focus:border-brand outline-none" data-testid="calc-lead-name" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => update("email", e.target.value)} className="px-4 py-3 border border-ink-line focus:border-brand outline-none" data-testid="calc-lead-email" />
          <input required type="tel" placeholder="Phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="px-4 py-3 border border-ink-line focus:border-brand outline-none md:col-span-2" data-testid="calc-lead-phone" />
          <textarea placeholder="Anything we should know? (optional)" value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} className="px-4 py-3 border border-ink-line focus:border-brand outline-none md:col-span-2" data-testid="calc-lead-message" />
        </div>
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <button type="button" onClick={onBack} className="btn-secondary">&lt; Edit inputs</button>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50" data-testid="calc-lead-submit">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Send my request <Send className="w-4 h-4" /></>}
          </button>
        </div>
        {error && (
          <div className="mt-5 border-l-4 border-destructive bg-destructive/5 p-4" data-testid="calc-error">
            <p className="font-heading font-bold text-destructive">Couldn't send your request.</p>
            <p className="text-sm text-ink-soft mt-1 leading-relaxed">
              Please try again in a moment, or call{" "}
              <a href="tel:+12488046933" className="font-bold text-ink underline">(248) 804-6933</a>{" "}
              and we'll take your info right over the phone.
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
